/**
 * A table's board: the job it is on — why it is wanted, what "done" means, its parts with one owner
 * each, and where every part stands. It sits above the composer while a job is open: one quiet line,
 * opened when you want to look. Agents work out of sight; this is where you see who is on what.
 */
import { useEffect, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import type { AtWork, BoardJob, BoardPart } from '@shared/types'
import type { Agent } from './data'
import { Btn, Glyph, Glyphs, Status, cx } from './kit'

/** How long something has been going on. */
export function Elapsed({ since }: { since: number }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(t)
  }, [])
  const s = Math.max(0, Math.floor((Date.now() - since) / 1000))
  return (
    <span className="tabular-nums">
      {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}
    </span>
  )
}

type Tone = 'free' | 'busy' | 'warn' | 'off' | 'down'
const JOB: Record<BoardJob['state'], { tone: Tone; word: string }> = {
  briefing: { tone: 'busy', word: 'Laying it out' },
  working: { tone: 'busy', word: 'Working' },
  closing: { tone: 'busy', word: 'Writing the answer' },
  checking: { tone: 'busy', word: 'Reading the answer' },
  waiting: { tone: 'warn', word: 'Waiting for you' },
  stopped: { tone: 'off', word: 'Stopped' },
  done: { tone: 'free', word: 'Done' },
}

function PartRow({ part, job, agents, at, onStop }: { part: BoardPart; job: BoardJob; agents: Agent[]; at?: AtWork; onStop: (agentId: string) => void }) {
  const owner = agents.find((a) => a.id === part.owner)
  const who = (id: string) => (id === 'user' ? 'you' : (agents.find((a) => a.id === id)?.name ?? 'someone'))
  const state =
    part.state === 'done' ? null : part.state === 'working' ? (
      <Status tone="busy">
        {part.doing ?? at?.doing ?? 'Working'}
        {(at?.since ?? part.startedAt) != null && (
          <span className="text-ws-fg-4">
            <Elapsed since={(at?.since ?? part.startedAt)!} />
          </span>
        )}
      </Status>
    ) : part.state === 'blocked' ? (
      <Status tone="warn">Waiting for {who(part.waits?.on ?? 'user')}</Status>
    ) : part.state === 'stopped' ? (
      <Status tone="off">Stopped</Status>
    ) : (
      <Status tone="off">{part.needs.length && job.parts.some((p) => part.needs.includes(p.n) && p.state !== 'done') ? `After ${part.needs.join(', ')}` : 'Not started'}</Status>
    )
  return (
    <li className="flex items-start gap-3 py-2">
      <span className="w-4 shrink-0 text-right text-[12px] tabular-nums leading-5 tracking-normal text-ws-fg-4">{part.n}</span>
      <span className="min-w-0 flex-1">
        <span className={cx('block leading-5 tracking-normal', part.state === 'done' ? 'text-ws-fg-3' : 'text-ws-fg')}>{part.title}</span>
        <span className="mt-[3px] flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1 text-[12px] leading-4 tracking-normal text-ws-fg-3">
          <span className="flex items-center gap-[6px] text-ws-fg-2">
            {owner && <Glyph palette={owner.palette} size={14} />}
            {owner?.name ?? '—'}
          </span>
          {part.state === 'done' ? (
            <span className="flex items-center gap-[5px]">
              <Check size={13} strokeWidth={2.2} aria-hidden />
              Done
            </span>
          ) : (
            state
          )}
          {part.state === 'working' && (
            <button type="button" onClick={() => onStop(part.owner)} className="-mx-[6px] flex h-5 items-center rounded-[5px] px-[6px] text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg">
              Stop {owner?.name ?? ''}
            </button>
          )}
        </span>
        {part.state === 'blocked' && part.waits && <span className="mt-[2px] block text-[12px] leading-4 tracking-normal text-ws-fg-3">{part.waits.why}</span>}
      </span>
    </li>
  )
}

export function Board({ job, agents, working, onStop, onStopAll, onDismiss }: { job: BoardJob; agents: Agent[]; working: AtWork[]; onStop: (agentId: string) => void; onStopAll: () => void; onDismiss: () => void }) {
  const [open, setOpen] = useState(false)
  const s = JOB[job.state]
  const done = job.parts.filter((p) => p.state === 'done').length
  const atWork = working.filter((w) => w.kind && w.kind !== 'react' && w.kind !== 'debrief')
  const lead = agents.find((a) => a.id === job.lead)
  const moving = job.state !== 'waiting' && job.state !== 'stopped'
  return (
    <div className="mb-2 animate-ws-in rounded-[10px] border border-ws-edge bg-ws-panel">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex h-10 w-full items-center gap-3 rounded-[10px] pl-3 pr-2 text-left">
        <Status tone={s.tone}>{s.word}</Status>
        <span className="min-w-0 flex-1 truncate tracking-normal text-ws-fg">{job.title}</span>
        {job.parts.length > 0 && (
          <span className="shrink-0 text-[12px] tabular-nums tracking-normal text-ws-fg-3">
            {done} of {job.parts.length}
          </span>
        )}
        {atWork.length > 0 && <Glyphs palettes={atWork.map((w) => agents.find((a) => a.id === w.id)?.palette ?? 'syntax')} max={4} />}
        <ChevronDown size={14} strokeWidth={2} className={cx('shrink-0 text-ws-icon transition-transform duration-150', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="border-t border-ws-edge px-3 pb-2 pt-3">
          <dl className="m-0 grid grid-cols-[72px_1fr] gap-x-3 gap-y-[6px] text-[12px] leading-[18px] tracking-normal">
            {job.why && (
              <>
                <dt className="text-ws-fg-4">Why</dt>
                <dd className="m-0 text-ws-fg-2">{job.why}</dd>
              </>
            )}
            {job.done && (
              <>
                <dt className="text-ws-fg-4">Done means</dt>
                <dd className="m-0 text-ws-fg-2">{job.done}</dd>
              </>
            )}
            {job.limits && (
              <>
                <dt className="text-ws-fg-4">Limits</dt>
                <dd className="m-0 text-ws-fg-2">{job.limits}</dd>
              </>
            )}
            <dt className="text-ws-fg-4">Led by</dt>
            <dd className="m-0 flex items-center gap-[6px] text-ws-fg-2">
              {lead && <Glyph palette={lead.palette} size={14} />}
              {lead?.name ?? '—'}
              <span className="text-ws-fg-4">· the final word is {job.decides === 'the user' ? 'yours' : `${job.decides}'s`}</span>
            </dd>
            {job.question && (
              <>
                <dt className="text-ws-fg-4">Asked you</dt>
                <dd className="m-0 text-ws-fg">{job.question}</dd>
              </>
            )}
          </dl>
          {job.parts.length > 0 && (
            <ul className="m-0 mt-2 list-none divide-y divide-ws-edge border-t border-ws-edge p-0">
              {job.parts.map((p) => (
                <PartRow key={p.n} part={p} job={job} agents={agents} at={working.find((w) => w.id === p.owner && w.part === p.n)} onStop={onStop} />
              ))}
            </ul>
          )}
          <div className="mt-1 flex h-8 items-center justify-end gap-2 border-t border-ws-edge pt-2">
            {moving ? (
              <Btn onPanel onClick={onStopAll}>
                Stop everyone
              </Btn>
            ) : (
              <>
                <span className="mr-auto text-[12px] tracking-normal text-ws-fg-3">{job.state === 'waiting' ? 'It goes on when you answer.' : 'Say “go on” to take it up again.'}</span>
                <Btn onPanel onClick={onDismiss}>
                  Put away
                </Btn>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
